/**
 * Setting up a game of klaverjassen with real cards.
 *
 * Two things to decide before the first deal: who is playing with whom, and
 * which rules. Teams are chosen here rather than falling out of the seating —
 * at a family table who your partner is matters more than who sits where.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Panel, Screen, Segmented, Toggle } from '@/components/ui';
import Avatar from '@/components/Avatar';
import { type KlaverjasOptions } from '@shared/game-logic';
import { useKlaverjasScore } from '@/store/klaverjasScore';
import { useScorekeeper } from '@/store/scorekeeper';
import { useRules } from '@/store/rules';

/** Seats 0 and 2 are one team, 1 and 3 the other — so this is the seat order. */
const SEAT_LABELS = ['Wij — speler 1', 'Zij — speler 1', 'Wij — speler 2', 'Zij — speler 2'];

export default function NewKlaverjas() {
  const navigate = useNavigate();
  const create = useKlaverjasScore((state) => state.createGame);
  const profiles = useScorekeeper((state) => state.profiles);

  const [names, setNames] = useState<string[]>(['', '', '', '']);
  // Rotterdams or Amsterdams is a household choice, not a per-potje one.
  const remembered = useRules((state) => state.klaverjas);
  const rememberKlaverjas = useRules((state) => state.rememberKlaverjas);
  const [options, setOptions] = useState<KlaverjasOptions>(remembered);
  const [dealer, setDealer] = useState(0);

  const complete = names.every((name) => name.trim().length > 0);

  return (
    <Screen
      title="Nieuw klaverjaspotje"
      back="/"
      subtitle="Twee tegen twee, maten tegenover elkaar"
    >
      <Panel title="Wie speelt met wie">
        <p className="mb-3 text-sm text-slate-400">
          "Wij" en "zij" zijn de twee teams. De maten zitten tegenover elkaar, dus de volgorde
          hieronder is ook de volgorde rond de tafel.
        </p>

        <ul className="space-y-2">
          {SEAT_LABELS.map((label, seat) => {
            const team = seat % 2;
            return (
              <li key={seat} className="flex items-center gap-2">
                <span
                  className={`w-2 self-stretch rounded-full ${
                    team === 0 ? 'bg-accent-500' : 'bg-slate-500'
                  }`}
                  aria-hidden
                />
                <div className="flex-1">
                  <label className="mb-1 block text-xs text-slate-500">{label}</label>
                  <input
                    value={names[seat]}
                    onChange={(event) => {
                      const next = [...names];
                      next[seat] = event.target.value;
                      setNames(next);
                    }}
                    list="klaverjas-namen"
                    maxLength={20}
                    placeholder="Naam"
                    className="min-h-12 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-base outline-none focus:border-accent-500"
                  />
                </div>
              </li>
            );
          })}
        </ul>

        <datalist id="klaverjas-namen">
          {profiles.map((profile) => (
            <option key={profile.id} value={profile.name} />
          ))}
        </datalist>

        {profiles.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                type="button"
                onClick={() => {
                  // Fill the first empty seat, so tapping four faces in order
                  // seats the whole table.
                  const index = names.findIndex((name) => name.trim() === '');
                  if (index === -1) return;
                  const next = [...names];
                  next[index] = profile.name;
                  setNames(next);
                }}
                className="flex items-center gap-1.5 rounded-full bg-base-700 px-2 py-1 text-sm hover:bg-base-600"
              >
                <Avatar player={{ name: profile.name, emoji: profile.emoji }} size={20} />
                {profile.name}
              </button>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Wie deelt de eerste gift">
        <Segmented
          value={String(dealer)}
          onChange={(value) => setDealer(Number(value))}
          options={names.map((name, seat) => ({
            value: String(seat),
            label: name.trim() || `Stoel ${seat + 1}`,
          }))}
        />
      </Panel>

      <Panel title="Regels">
        <div className="space-y-4">
          <div>
            <p className="mb-1 text-sm font-medium">Variant</p>
            <Segmented
              value={options.variant}
              onChange={(variant) => setOptions({ ...options, variant })}
              options={[
                { value: 'rotterdams', label: 'Rotterdams' },
                { value: 'amsterdams', label: 'Amsterdams' },
              ]}
            />
            <p className="mt-1 text-xs text-slate-500">
              {options.variant === 'rotterdams'
                ? 'Kun je niet bekennen, dan moet je altijd troeven — ook als je maat de slag al heeft.'
                : 'Heeft je maat de slag, dan hoef je niet te troeven.'}
            </p>
          </div>

          <div>
            <p className="mb-1 text-sm font-medium">Troef kiezen</p>
            <Segmented
              value={options.trumpChoice}
              onChange={(trumpChoice) => setOptions({ ...options, trumpChoice })}
              options={[
                { value: 'verplicht', label: 'Verplicht' },
                { value: 'vrij', label: 'Mag passen' },
              ]}
            />
            <p className="mt-1 text-xs text-slate-500">
              {options.trumpChoice === 'verplicht'
                ? 'De speler links van de deler moet kiezen.'
                : 'Passen mag; is iedereen langs geweest, dan moet de deler kiezen.'}
            </p>
          </div>

          <div>
            <p className="mb-1 text-sm font-medium">Lengte</p>
            <Segmented
              value={options.length.kind === 'deals' ? String(options.length.deals) : 'punten'}
              onChange={(value) =>
                setOptions({
                  ...options,
                  length:
                    value === 'punten'
                      ? { kind: 'points', target: 1500 }
                      : { kind: 'deals', deals: Number(value) },
                })
              }
              options={[
                { value: '8', label: '8 giften' },
                { value: '16', label: '16 giften' },
                { value: 'punten', label: 'Tot 1500' },
              ]}
            />
          </div>

          <Toggle
            checked={options.pitBonus}
            onChange={(pitBonus) => setOptions({ ...options, pitBonus })}
            label="Pit telt 100 extra"
            hint="Voor de partij die alle acht slagen pakt."
          />
        </div>
      </Panel>

      <Button
        full
        variant="primary"
        disabled={!complete}
        onClick={() => {
          const id = create({
            seats: names.map((name) => ({ displayName: name, profileId: null })),
            options,
            firstDealerSeat: dealer,
          });
          rememberKlaverjas(options);
          navigate(`/klaverjas/${id}`, { replace: true });
        }}
      >
        {complete ? 'Beginnen' : 'Vul vier namen in'}
      </Button>
    </Screen>
  );
}
